import { useState } from 'react'
import {
  Box,
  Table,
  Text,
  Badge,
  IconButton,
  HStack,
  Portal,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
} from '@chakra-ui/react'
import { MoreVertical, FileCheck, Send, Trash2 } from 'lucide-react'
import { useFundCalls, useDeleteFundCall, useIssueFundCall, useSendFundCall } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog'
import type { FundCallStatus, FundCallBudget } from '../types'

interface FundCallTableProps {
  residenceId: string
  fiscalYearId?: string
  selectedFundCallId?: string | null
  onFundCallClick?: (fundCallId: string) => void
}

export function FundCallTable({
  residenceId,
  fiscalYearId,
  selectedFundCallId,
  onFundCallClick,
}: FundCallTableProps) {
  const { data: fundCalls, isLoading } = useFundCalls(residenceId, fiscalYearId)
  const deleteFundCall = useDeleteFundCall()
  const issueFundCall = useIssueFundCall()
  const sendFundCall = useSendFundCall()

  // Dialog states
  const [issueDialogId, setIssueDialogId] = useState<string | null>(null)
  const [sendDialogId, setSendDialogId] = useState<string | null>(null)
  const [deleteDialogId, setDeleteDialogId] = useState<string | null>(null)

  const getStatusBadge = (status: FundCallStatus) => {
    const config: Record<FundCallStatus, { color: string; label: string }> = {
      draft: { color: 'gray', label: 'Brouillon' },
      issued: { color: 'purple', label: 'Émis' },
      sent: { color: 'blue', label: 'Envoyé' },
      partial: { color: 'orange', label: 'Partiellement payé' },
      paid: { color: 'green', label: 'Payé' },
      overdue: { color: 'red', label: 'En retard' },
    }
    const { color, label } = config[status] || { color: 'gray', label: status }
    return (
      <Badge colorPalette={color} variant="subtle">
        {label}
      </Badge>
    )
  }

  const formatCurrency = (value: number | null) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR')
  }

  const renderBudgets = (fundCallBudgets?: FundCallBudget[]) => {
    if (!fundCallBudgets || fundCallBudgets.length === 0) {
      return <Text color="gray.400">-</Text>
    }

    return (
      <HStack gap={1} flexWrap="wrap">
        {fundCallBudgets.slice(0, 2).map((fcb) => (
          <Badge key={fcb.id} size="sm" variant="subtle">
            {fcb.budget?.name}
          </Badge>
        ))}
        {fundCallBudgets.length > 2 && (
          <Badge size="sm" variant="outline" colorPalette="gray">
            +{fundCallBudgets.length - 2}
          </Badge>
        )}
      </HStack>
    )
  }

  const handleIssue = async () => {
    if (!issueDialogId) return
    try {
      await issueFundCall.mutateAsync(issueDialogId)
      toaster.create({
        title: 'Appel de fonds émis',
        description: 'L\'appel de fonds a été comptabilisé avec succès.',
        type: 'success',
      })
    } catch (error) {
      console.error('Error issuing fund call:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'émission.',
        type: 'error',
      })
    } finally {
      setIssueDialogId(null)
    }
  }

  const handleSend = async () => {
    if (!sendDialogId) return
    try {
      await sendFundCall.mutateAsync(sendDialogId)
      toaster.create({
        title: 'Appel de fonds envoyé',
        description: 'L\'appel de fonds a été envoyé aux copropriétaires.',
        type: 'success',
      })
    } catch (error) {
      console.error('Error sending fund call:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'envoi.',
        type: 'error',
      })
    } finally {
      setSendDialogId(null)
    }
  }

  const handleDelete = async () => {
    if (!deleteDialogId) return
    try {
      await deleteFundCall.mutateAsync(deleteDialogId)
      toaster.create({
        title: 'Appel de fonds supprimé',
        description: 'L\'appel de fonds a été supprimé avec succès.',
        type: 'success',
      })
    } catch (error) {
      console.error('Error deleting fund call:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression.',
        type: 'error',
      })
    } finally {
      setDeleteDialogId(null)
    }
  }

  const handleRowClick = (fundCallId: string) => {
    onFundCallClick?.(fundCallId)
  }

  const fundCallToDelete = fundCalls?.find((fc) => fc.id === deleteDialogId)

  if (isLoading) {
    return <Text>Chargement...</Text>
  }

  if (!fundCalls || fundCalls.length === 0) {
    return (
      <Box textAlign="center" py={8} color="gray.500">
        <Text>Aucun appel de fonds pour cet exercice</Text>
      </Box>
    )
  }

  return (
    <>
      <Box overflowX="auto">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Nom</Table.ColumnHeader>
              <Table.ColumnHeader>Budgets</Table.ColumnHeader>
              <Table.ColumnHeader>Date émission</Table.ColumnHeader>
              <Table.ColumnHeader>Date échéance</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Montant total</Table.ColumnHeader>
              <Table.ColumnHeader>Statut</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {fundCalls.map((fundCall) => (
              <Table.Row
                key={fundCall.id}
                data-fund-call-row
                cursor="pointer"
                onClick={() => handleRowClick(fundCall.id)}
                bg={selectedFundCallId === fundCall.id ? 'brand.50' : 'transparent'}
                _hover={{ bg: selectedFundCallId === fundCall.id ? 'brand.100' : 'gray.50' }}
                transition="background 0.15s"
              >
                <Table.Cell fontWeight="medium">{fundCall.label}</Table.Cell>
                <Table.Cell>{renderBudgets(fundCall.fund_call_budgets)}</Table.Cell>
                <Table.Cell>{formatDate(fundCall.call_date)}</Table.Cell>
                <Table.Cell>{formatDate(fundCall.due_date)}</Table.Cell>
                <Table.Cell textAlign="right" fontWeight="semibold">
                  {formatCurrency(fundCall.total_amount)}
                </Table.Cell>
                <Table.Cell>{getStatusBadge(fundCall.status)}</Table.Cell>
                <Table.Cell>
                  <HStack justify="flex-end" onClick={(e) => e.stopPropagation()}>
                    <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
                      <MenuTrigger asChild>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label="Actions"
                        >
                          <MoreVertical size={16} />
                        </IconButton>
                      </MenuTrigger>
                      <Portal>
                        <MenuPositioner>
                          <MenuContent minW="180px">
                            {/* Émettre - disponible pour les brouillons */}
                            {fundCall.status === 'draft' && (
                              <MenuItem
                                value="issue"
                                onClick={() => setIssueDialogId(fundCall.id)}
                              >
                                <HStack gap={2}>
                                  <FileCheck size={16} />
                                  <Text>Émettre</Text>
                                </HStack>
                              </MenuItem>
                            )}

                            {/* Envoyer - disponible pour les appels émis */}
                            {fundCall.status === 'issued' && (
                              <MenuItem
                                value="send"
                                onClick={() => setSendDialogId(fundCall.id)}
                              >
                                <HStack gap={2}>
                                  <Send size={16} />
                                  <Text>Envoyer</Text>
                                </HStack>
                              </MenuItem>
                            )}

                            {/* Supprimer - uniquement pour les brouillons */}
                            {fundCall.status === 'draft' && (
                              <MenuItem
                                value="delete"
                                onClick={() => setDeleteDialogId(fundCall.id)}
                                color="red.600"
                                _hover={{ bg: 'red.50' }}
                              >
                                <HStack gap={2}>
                                  <Trash2 size={16} />
                                  <Text>Supprimer</Text>
                                </HStack>
                              </MenuItem>
                            )}
                          </MenuContent>
                        </MenuPositioner>
                      </Portal>
                    </MenuRoot>
                  </HStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Dialog Émettre */}
      <ConfirmDialog
        isOpen={!!issueDialogId}
        onClose={() => setIssueDialogId(null)}
        onConfirm={handleIssue}
        title="Émettre l'appel de fonds"
        message="Cette action va comptabiliser l'appel de fonds (crédit 701/102/105, débit 450xxx). Voulez-vous continuer ?"
        confirmLabel="Émettre"
        colorPalette="purple"
        icon={<FileCheck size={24} />}
        isLoading={issueFundCall.isPending}
      />

      {/* Dialog Envoyer */}
      <ConfirmDialog
        isOpen={!!sendDialogId}
        onClose={() => setSendDialogId(null)}
        onConfirm={handleSend}
        title="Envoyer l'appel de fonds"
        message="Voulez-vous envoyer cet appel de fonds aux copropriétaires ?"
        confirmLabel="Envoyer"
        colorPalette="blue"
        icon={<Send size={24} />}
        isLoading={sendFundCall.isPending}
      />

      {/* Dialog Supprimer */}
      {fundCallToDelete && (
        <ConfirmDeleteDialog
          isOpen={!!deleteDialogId}
          onClose={() => setDeleteDialogId(null)}
          onConfirm={handleDelete}
          entityName={fundCallToDelete.label}
          entityType="appel de fonds"
        />
      )}
    </>
  )
}
